import React, { useEffect, useState } from 'react';
import { UseFormGetValues } from 'react-hook-form';

import { Content, Progress } from '@backstage/core-components';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';

import { Paper } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { tomtomLabApiRef } from '../../api';
import {
  Assessment,
  AssessmentType,
  Criteria,
  JuryAssessmentFormValues,
  Score,
} from '../../types';
import { useCanJuryScore } from '../../util/juryAssessmentMethods';
import { useIdea } from '../CreateIdea/useIdea';
import { PageTop } from '../PageTop';
import { Form } from './Form';
import { useAssessment } from './useAssessment';

type JuryAssessmentProps = {
  displaySummary?: boolean;
  ideaId?: number;
};

export const JuryAssessment = ({
  displaySummary,
  ideaId,
}: JuryAssessmentProps) => {
  const tomtomLabApi = useApi(tomtomLabApiRef);
  const alertApi = useApi(alertApiRef);
  const { idea: idea } = useIdea(tomtomLabApi);
  const { assessment: selfAssessment } = useAssessment(tomtomLabApi, 'SELF');
  const canJuryScore = useCanJuryScore(idea);

  const { assessment: juryAssessment, userRef } = useAssessment(
    tomtomLabApi,
    'JURY',
    ideaId,
  );

  const [defaultValues, setDefaultValues] = useState<
    JuryAssessmentFormValues | undefined
  >(undefined);

  useEffect(() => {
    if (juryAssessment) {
      const techFeasibilityScore = juryAssessment.scores.find(
        score => score.criteria === ('Technical Feasibility' as Criteria),
      );
      const strategicFitScore = juryAssessment.scores.find(
        score => score.criteria === ('Strategic Fit' as Criteria),
      );
      const financialPotentialScore = juryAssessment.scores.find(
        score => score.criteria === ('Financial Potential' as Criteria),
      );
      const innovativenessScore = juryAssessment.scores.find(
        score => score.criteria === ('Innovativeness' as Criteria),
      );

      const defaultValuesToSet: JuryAssessmentFormValues = {
        techFeasibility: techFeasibilityScore?.description,
        techFeasibilityScore: String(techFeasibilityScore?.score),
        strategicFit: strategicFitScore?.description,
        strategicFitScore: String(strategicFitScore?.score),
        financialPotential: financialPotentialScore?.description,
        financialPotentialScore: String(financialPotentialScore?.score),
        innovativeness: innovativenessScore?.description,
        innovativenessScore: String(innovativenessScore?.score),
      };
      setDefaultValues(defaultValuesToSet);
    }
  }, [idea, selfAssessment, juryAssessment]);

  const postAlert = ({
    severity,
    register,
  }: {
    severity: 'success' | 'error';
    register: boolean;
  }) => {
    const successActionInMessage = register ? 'assessed' : 'assessment updated';
    const failureActionInMessage = register
      ? 'assess'
      : 'update the assessment of';
    const message =
      severity === 'success'
        ? `Idea ${successActionInMessage} successfully`
        : `An error has occurred trying to ${failureActionInMessage} the idea. Please try again later.`;
    alertApi.post({
      severity,
      message,
    });
  };

  const createScoresFromFormValues = (
    formValues: JuryAssessmentFormValues,
  ): Score[] => {
    const scores: Score[] = [];

    const techFeasibility = {
      criteria: 'Technical Feasibility' as Criteria,
      description: formValues.techFeasibility,
      score: parseFloat(formValues.techFeasibilityScore ?? '0'),
    } as Score;
    scores.push(techFeasibility);

    const strategicFit = {
      criteria: 'Strategic Fit' as Criteria,
      description: formValues.strategicFit,
      score: parseFloat(formValues.strategicFitScore ?? '0'),
    } as Score;
    scores.push(strategicFit);

    const financialPotential = {
      criteria: 'Financial Potential' as Criteria,
      description: formValues.financialPotential,
      score: parseFloat(formValues.financialPotentialScore ?? '0'),
    } as Score;
    scores.push(financialPotential);

    const innovativeness = {
      criteria: 'Innovativeness' as Criteria,
      description: formValues.innovativeness,
      score: parseFloat(formValues.innovativenessScore ?? '0'),
    } as Score;
    scores.push(innovativeness);

    return scores;
  };

  const calculateTotalScore = (scores: Score[]): number => {
    let total = 0;
    let populatedScores = 0;
    scores.forEach(score => {
      if (score.score !== undefined) {
        total += score.score;
        populatedScores++;
      }
    });
    const totalScore = total / populatedScores;
    return Math.round((totalScore + Number.EPSILON) * 10) / 10;
  };

  const createAssessmentFromFormValues = (
    formValues: JuryAssessmentFormValues,
  ): Assessment => {
    const scores = createScoresFromFormValues(formValues);
    return {
      id: juryAssessment?.id,
      ideaId: ideaId ?? idea?.id,
      scoredBy: userRef,
      scores: scores,
      type: 'JURY' as AssessmentType,
      totalScore: calculateTotalScore(scores),
    } as Assessment;
  };

  const handleSubmit: (
    getValues: UseFormGetValues<JuryAssessmentFormValues>,
  ) => Promise<void> = async (
    getValues: UseFormGetValues<JuryAssessmentFormValues>,
  ) => {
    const formValues = getValues();

    const assessment = createAssessmentFromFormValues(formValues);
    let assessmentResponse;
    try {
      if (juryAssessment) {
        assessmentResponse = await tomtomLabApi.updateAssessment(assessment);
      } else {
        assessmentResponse = await tomtomLabApi.createAssessment(assessment);
      }
    } catch {
      assessmentResponse = undefined;
    }
    if (assessmentResponse?.status !== 'ok') {
      postAlert({ severity: 'error', register: false });
    } else {
      postAlert({ severity: 'success', register: false });
    }
  };

  const JuryAssessmentForm = (
    <Form
      handleSave={handleSubmit}
      idea={idea}
      selfAssessment={selfAssessment}
      defaultValues={defaultValues}
      handleClose={() => {}}
      displaySummary={displaySummary ?? true}
    />
  );

  const renderPageBody = () => {
    if (idea === undefined) {
      return <Progress />;
    }
    if (!canJuryScore) {
      return (
        <Alert severity="info">Jury scoring is not open for this idea.</Alert>
      );
    }
    return <Paper style={{ padding: '25px' }}>{JuryAssessmentForm}</Paper>;
  };

  // The embedded form is already gated by its parent (CardContentFields); the
  // standalone page is reachable by URL, so it checks the gate itself.
  return (
    <Content noPadding>
      {displaySummary || displaySummary === undefined ? (
        <>
          <PageTop pageTitle="Jury Assessment" />
          {renderPageBody()}
        </>
      ) : (
        JuryAssessmentForm
      )}
    </Content>
  );
};
